import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { connect } from "react-redux";
import { Link, Redirect } from "react-router-dom";
import { ARTICLES, CATEGORIES, SECTIONS, findCategory, findSet } from "./guidedBiddingData";
import { loadSetGrants, loadResults, questionKey, recordQuestion, recordHandDone } from "./guidedBiddingResults";
import "./GuidedBidding.css";

/*
 * Guided bidding (Just Play): pick a hand type, bid it one call at a time with
 * two attempts per bid, then read Paul's commentary. All bridge text lives,
 * verbatim, in guidedBiddingData.js.
 *
 *   /just-play/guided-bidding                     categories (1NT live, the rest "Coming soon")
 *   /just-play/guided-bidding/:cat                that category's sets
 *   /just-play/guided-bidding/:cat/:set?hand=N    the trainer (hand N, 1-based, linkable)
 */

const ROOT = "/just-play/guided-bidding";
// Points per HAND finished, first time only, right or wrong (Paul, 26 Sep 2026: "each hand should
// give 2 points ... not each question").
// KEEP THE SAME as POINTS.guided in content-app/lib/thisMonth.js (that is what the ladder counts).
const GUIDED_POINTS = 2;
// Practice-set questions score 1 (Paul, 7 Oct 2026). Keep both in step with content-app POINTS.guided / guidedPractice.
const PRACTICE_POINTS = 1;
// Opened inside a Game Plan panel (an iframe): no breadcrumb, "Back to Game Plan" closes the panel,
// and anything that has to leave (membership, log in, go yearly) opens in the whole window.
const FRAMED = typeof window !== "undefined" && window.self !== window.top;
const tellParent = (type) => {
  try {
    window.parent.postMessage({ type }, window.location.origin);
  } catch (e) {
    /* not framed by us */
  }
};
const ORDER = ["C", "D", "H", "S", "N"];
const SUIT_CHAR = { C: "♣", D: "♦", H: "♥", S: "♠" };
const SUIT_CLASS = { C: "gb-c", D: "gb-d", H: "gb-h", S: "gb-s" };

const suitSpan = (s) => `<span class="${SUIT_CLASS[s]}">${SUIT_CHAR[s]}</span>`;
const rank = (b) => (Number(b[0]) - 1) * 5 + ORDER.indexOf(b.slice(1));
const isBid = (c) => /^[1-7][CDHSN]$/.test(c);

function callHtml(c) {
  if (c === "-") return "";
  if (c === "P") return "Pass";
  if (c === "X" || c === "XX") return c;
  return c[0] + (c[1] === "N" ? "NT" : suitSpan(c[1]));
}

const escapeHtml = (t) => String(t).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// "3C" -> 3♣ in text; "3NT" stays.
// Raw glyphs typed into the text (e.g. "Transfer to ♥") are coloured too.
const GLYPH_SUIT = { "♣": "C", "♦": "D", "♥": "H", "♠": "S" };
const suits = (t) =>
  String(t)
    .replace(/[♣♦♥♠]/g, (g) => suitSpan(GLYPH_SUIT[g]))
    .replace(/(\d)([CDHS])(?![a-z])/g, (m, l, s) => l + suitSpan(s));

// Label text such as "1H and 1S opening" -> React nodes with coloured suits.
function Suited({ text }) {
  if (text == null || text === "") return null;
  return <span dangerouslySetInnerHTML={{ __html: suits(escapeHtml(text)) }} />;
}

/**
 * Commentary text -> HTML. Bare bids already in the auction link to their latest call;
 * [[i|phrase]] links a phrase to call i; {{art|text}} opens the article.
 */
function commentHtml(text, calls) {
  // {{plain|2S}}: a bid shown with its suit but NOT linked - a hypothetical bid, not the one in the auction.
  let t = escapeHtml(text).replace(/\{\{plain\|([1-7])([CDHS]|NT)\}\}/g, (m, l, x) => `${l}\u0006${x === "NT" ? "N" : x}`).replace(/\{\{art(?::(\w+))?\|([^}]+)\}\}/g, (m, k, x) => `\u0002${k || "1"}\u0004${x}\u0003`);
  const out = t
    .split(/(\[\[\d+\|[^\]]+\]\])/)
    .map((part) => {
      const m = part.match(/^\[\[(\d+)\|([^\]]+)\]\]$/);
      if (m) return `<span class="gb-ref" tabindex="0" data-ref="${m[1]}">${suits(m[2])}</span>`;
      const marked = part.replace(/\b([1-7])(NT|C|D|H|S)\b/g, (b, l, s) => {
        const i = calls.lastIndexOf(l + (s === "NT" ? "N" : s));
        return i < 0 ? b : `\u0000${i}|${b}\u0001`;
      });
      return suits(marked).replace(
        /\u0000(\d+)\|([^\u0001]+)\u0001/g,
        (mm, i, x) => `<span class="gb-ref" tabindex="0" data-ref="${i}">${x}</span>`
      );
    })
    .join("");
  return out
    .replace(/\u0006([CDHSN])/g, (m, x) => (x === "N" ? "NT" : suitSpan(x)))
    .replace(/\u0002(\w+)\u0004([^\u0003]+)\u0003/g, (m, k, x) => `<a href="#" class="gb-artref" data-art="${k}">${x}</a>`)
    // {{em|text}}: a phrase Paul wants emphasised with colour (2 over 1 set, 9 Oct 2026: "Frequently respond 1NT").
    .replace(/\{\{em\|([^}]+)\}\}/g, (m, x) => `<mark class="gb-em">${x}</mark>`);
}

// Commentary laid out for reading (Paul, 28 Sep 2026: "just one big bunch of words, make it look nice / spaced").
// Paul's words are untouched: the first sentence becomes a bold lead line, the rest short paragraphs of up to two
// sentences, and "(article)" references move out of the sentence into link buttons underneath. A comment that
// already contains "\n\n" keeps its own paragraph breaks.
function commentLayout(text, calls) {
  const refs = [];
  const body = String(text || "").replace(/\s*\(\{\{art(?::(\w+))?\|([^}]+)\}\}\)/g, (m, k, x) => {
    refs.push({ k: k || "1", x });
    return "";
  });
  let paras;
  if (/\n\s*\n/.test(body)) paras = body.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  else {
    const sentences = body.split(/(?<=[.!?])\s+(?=["'A-Z0-9])/).map((p) => p.trim()).filter(Boolean);
    paras = sentences.length ? [sentences[0]] : [];
    for (let i = 1; i < sentences.length; i += 2) paras.push(sentences.slice(i, i + 2).join(" "));
  }
  // A paragraph starting "RULE: " is one of Paul's rules, shown as a rule box where it sits in the commentary
  // (1H/1S set, 30 Sep 2026: several rules inside one comment). Single line breaks are kept (numbered lists).
  // Paul, 14 Oct 2026: "the rules should stand out more ... reduce the monotony of reading": "Rule:" in any case is a
  // rule box; "1." paragraphs get a number badge; a paragraph starting "*" is a side note.
  const html = paras
    .map((p, i) => {
      if (/^rule:\s*/i.test(p)) return `<div class="gb-rule gb-rule--inline"><b>Rule</b> ${commentHtml(p.replace(/^rule:\s*/i, ""), calls)}</div>`;
      const num = p.match(/^(\d+)\.\s+([\s\S]*)$/);
      if (num) return `<div class="gb-cm-num"><span class="gb-cm-n">${num[1]}</span><p>${commentHtml(num[2], calls).replace(/\n/g, "<br>")}</p></div>`;
      if (/^\*/.test(p)) return `<p class="gb-cm-note">${commentHtml(p.replace(/^\*\s*/, ""), calls).replace(/\n/g, "<br>")}</p>`;
      return `<p class="${i === 0 && paras.length > 1 ? "gb-cm-lead" : "gb-cm-p"}">${commentHtml(p, calls).replace(/\n/g, "<br>")}</p>`;
    })
    .join("");
  const chips = refs.length
    ? `<div class="gb-artchips">${refs
        .map((r) => `<a href="#" class="gb-artref gb-artchip" data-art="${r.k}">📖 ${escapeHtml(r.x)}</a>`)
        .join("")}</div>`
    : "";
  return html + chips;
}

function youtubeId(url) {
  const m = String(url || "").match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : null;
}

// Fanned playing cards, BBO style (Paul, 7 Oct 2026: "more intuitive for people to read"). Suits in the
// order S H C D (alternating colours - Paul, 9 Oct 2026: "red on red is difficult to see", site-wide), each card
// overlapping the next with its rank and suit showing in the top-left corner.
// The overlap is a grid of narrow columns, so the fan squeezes to fit any width (phone included).
function HandRows({ hand, small, suits: order = ["S", "H", "C", "D"] }) {
  const cards = [];
  order.forEach((s) => {
    String(hand[s] || "")
      .split(/\s+/)
      .filter(Boolean)
      .forEach((r) => cards.push({ s, r: r === "T" ? "10" : r }));
  });
  const n = cards.length;
  const label = order.map((s) => `${SUIT_CHAR[s]} ${hand[s] || "void"}`).join(", ");
  return (
    <div
      className={`gb-fan${small ? " gb-fan--small" : ""}`}
      style={{ gridTemplateColumns: `repeat(${Math.max(n - 1, 0)}, minmax(0, var(--gb-step))) var(--gb-cw)` }}
      role="img"
      aria-label={label}
    >
      {cards.map((c, i) => (
        <span key={i} className={`gb-pc ${SUIT_CLASS[c.s]}${i > 0 && cards[i - 1].s !== c.s ? " gb-pc--newsuit" : ""}`} style={{ gridColumn: i + 1 }}>
          <span className="gb-pc-r">{c.r}</span>
          <span className="gb-pc-s">{SUIT_CHAR[c.s]}</span>
        </span>
      ))}
    </div>
  );
}

export function Video({ url, tier, isMember, isAdmin, free }) {
  const id = youtubeId(url);
  if (!id) return null;
  // Premium and the yearly plans (Premium Yearly, Partnership Yearly tiers all start "premium").
  // `free`: the first hand of every set is open to everyone, its video included (Paul, 27 Sep 2026:
  // "for the free hands, make the video also available to everyone, only for that first one").
  if (free || isAdmin || String(tier || "").startsWith("premium")) {
    return (
      <div className="gb-video">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1`}
          title="Video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  // Not Premium: offer both ways in - Premium, and the yearly plan (Paul, 26 Sep 2026: "give them the
  // option of going yearly, not just upgrading to premium"). A paying basic member switches with the
  // two-click /go-yearly; anyone else lands on the membership page opened at the yearly plan.
  return (
    <div className="gb-videogate">
      <span className="gb-videogate-play" aria-hidden="true">🔒</span>
      <span className="gb-videogate-body">
        <b>Premium video</b>
        <span className="gb-videogate-sub">For Premium and yearly members.</span>
        <span className="gb-videogate-btns">
          <a className="gb-videogate-btn" href="/membership" target="_top">
            {isMember ? "Upgrade to Premium" : "View membership"}
          </a>
          <a className="gb-videogate-btn gb-videogate-btn--yearly" href={isMember ? "/go-yearly" : "/membership?plan=yearly"} target="_top">
            Best value: go yearly →
          </a>
        </span>
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ pages */

// A set marked draft:true is shown to admins only, so Paul can proofread it on the live site before it is published.
// Drafts also show on the local dev copy (localhost), which never affects the live site.
const DEV_HOST = typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
const visibleSets = (cat, isAdmin) => (cat ? cat.sets.filter((x) => !x.draft || isAdmin || DEV_HOST) : []);
// Practice sets sit under their category's sets (Paul, 7 Oct 2026: "underneath those ... attached to those").
const visiblePractice = (cat, isAdmin) => (cat && cat.practice ? cat.practice.filter((x) => !x.draft || isAdmin || DEV_HOST) : []);

// One row per category. A category with no visible sets shows as "Coming soon".
function CatCard({ c, isAdmin }) {
  const n = visibleSets(c, isAdmin).length;
  if (!n) {
    return (
      <div className="gb-cat gb-cat--soon" aria-disabled="true">
        <span className="gb-cat-label">
          <Suited text={c.label} />
        </span>
        <span className="gb-soon">Coming soon</span>
      </div>
    );
  }
  const hands = visibleSets(c, isAdmin).reduce((t, x) => t + x.hands.length, 0);
  return (
    <Link to={`${ROOT}/${c.id}`} className="gb-cat gb-cat--live">
      <span className="gb-cat-badge">
        <Suited text={c.badge || c.label} />
      </span>
      <span className="gb-cat-main">
        <span className="gb-cat-label">
          <Suited text={c.label} />
        </span>
        <span className="gb-cat-count">
          {n} {n === 1 ? "set" : "sets"} · {hands} hands
        </span>
      </span>
      <span className="gb-arrow" aria-hidden="true">→</span>
    </Link>
  );
}

// Consecutive categories with the same `group` become one block; the block's practice sets (shared, so
// de-duplicated by id) sit once underneath them.
function homeBlocks(isAdmin) {
  const blocks = [];
  CATEGORIES.forEach((c) => {
    const last = blocks[blocks.length - 1];
    if (c.group && last && last.group === c.group) last.cats.push(c);
    else blocks.push({ group: c.group || null, cats: [c], practice: [] });
  });
  blocks.forEach((b) => {
    const seen = new Set();
    b.cats.forEach((c) => visiblePractice(c, isAdmin).forEach((p) => { if (!seen.has(p.id)) { seen.add(p.id); b.practice.push(p); } }));
  });
  return blocks;
}

function Home({ isAdmin }) {
  return (
    <div className="gb-page gb-page--narrow">
      <Helmet>
        <title>Guided bidding — Bridge Champions</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="gb-crumb">
        <Link to="/just-play">Just Play</Link> ›
      </div>
      <h1 className="gb-title">Guided bidding</h1>
      {/* Two sections, Constructive and Competitive (Paul, 14 Oct 2026: "lets make that whole area more organised"). Each has its
          live sets, then its own "Coming soon" tiles. */}
      {SECTIONS.map((sec) => {
        const blocks = homeBlocks(isAdmin).filter((bl) => (bl.cats[0].section || "constructive") === sec.id && bl.cats.some((c) => visibleSets(c, isAdmin).length));
        const soon = CATEGORIES.filter((c) => (c.section || "constructive") === sec.id && !visibleSets(c, isAdmin).length);
        if (!blocks.length && !soon.length) return null;
        return (
          <section key={sec.id} className="gb-section">
            <h2 className="gb-section-h">{sec.label}</h2>
            <div className="gb-cats">
              {blocks.map((bl) => (
                // Every live block sits in its own panel, so 1NT matches the grouped openings (Paul, 7 Oct 2026).
                <div key={bl.cats[0].id} className="gb-group">
                  {bl.cats.map((c) => <CatCard key={c.id} c={c} isAdmin={isAdmin} />)}
                  {bl.practice.map((p) => (
                    <Link key={p.id} to={`${ROOT}/${bl.cats[0].id}/${p.id}`} className="gb-pcard">
                      <span className="gb-pcard-ic" aria-hidden="true">✎</span>
                      <span className="gb-pcard-main">
                        <span className="gb-pcard-k">Practice</span>
                        <span className="gb-pcard-t"><Suited text={p.title} /></span>
                        <span className="gb-pcard-m">
                          {p.hands.length} questions{bl.cats.length > 1 ? ` · for the ${bl.cats.length === 2 ? "two" : bl.cats.length} openings above` : ""}
                        </span>
                      </span>
                      <span className="gb-pcard-go">Start →</span>
                    </Link>
                  ))}
                </div>
              ))}
            </div>
            {soon.length ? (
              <>
                {/* Not built yet: small muted tiles, so the live sets lead (Paul, 7 Oct 2026). */}
                <div className="gb-soonhead">Coming soon</div>
                <div className="gb-soongrid">
                  {soon.map((c) => (
                    <div key={c.id} className="gb-soontile" aria-disabled="true">
                      <Suited text={c.label} />
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

export const GuidedBiddingHome = connect((state) => ({ isAdmin: state.auth?.a === true }))(Home);

function handStatus(results, setId, h) {
  const rs = h.steps.map((st) => results[questionKey(setId, h.id, st.id)]).filter(Boolean);
  if (!rs.length) return "new";
  if (rs.some((r) => r.last && r.last !== "first")) return "miss";
  return rs.length === h.steps.length ? "good" : "part";
}

function Category({ match, uid, isAdmin, authReady }) {
  const cat = findCategory(match.params.cat);
  const sets = visibleSets(cat, isAdmin);
  const [results, setResults] = useState({});
  useEffect(() => {
    let live = true;
    loadResults(uid).then((r) => live && setResults(r));
    return () => {
      live = false;
    };
  }, [uid]);
  // Paul, 14 Oct 2026: clicking a card low on the home page kept the scroll position, so the short category
  // page opened scrolled past its content ("an empty screen which I'm at the bottom of").
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [match.params.cat]);
  if (cat && !sets.length && cat.sets.some((x) => x.draft) && !authReady) return null;
  if (!cat || !sets.length) return <Redirect to={ROOT} />;
  return (
    <div className="gb-page gb-page--mid">
      <Helmet>
        <title>{`${cat.label} — Guided bidding — Bridge Champions`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="gb-crumb">
        <Link to="/just-play">Just Play</Link> › <Link to={ROOT}>Guided bidding</Link> ›
      </div>
      <div className="gb-cat-head">
        <div>
          <div className="gb-kicker">Guided bidding</div>
          <h1 className="gb-title gb-title--left gb-title--flush">
            <Suited text={cat.label} />
          </h1>
        </div>
      </div>
      {cat.guide && ARTICLES[cat.guide] ? (
        <a className="gb-guide" href={ARTICLES[cat.guide].path}>
          <span className="gb-guide-ic" aria-hidden="true">📘</span>
          <span className="gb-guide-t">
            <span className="gb-guide-k">General guide</span>
            <Suited text={ARTICLES[cat.guide].title} />
          </span>
          <span className="gb-guide-open">Read ›</span>
        </a>
      ) : null}
      <div className="gb-sets">
        {sets.map((s) => {
          const status = s.hands.map((h) => handStatus(results, s.id, h));
          const played = status.filter((x) => x !== "new").length;
          const nextIdx = status.findIndex((x) => x === "new" || x === "part");
          const to = `${ROOT}/${cat.id}/${s.id}${played ? `?hand=${(nextIdx < 0 ? 0 : nextIdx) + 1}` : ""}`;
          return (
            <Link key={s.id} to={to} className="gb-set">
              <span className="gb-badge">
                {/* Short form when the label is long ("1C and 1D opening" overflowed the square). */}
                <Suited text={cat.badge || cat.label} />
              </span>
              <span className="gb-set-main">
                <span className="gb-set-title"><Suited text={s.title} />{s.draft ? <span className="gb-draft"> Draft · admins only</span> : null}</span>
                <span className="gb-set-dots" aria-label={`${played} of ${s.hands.length} hands played`}>
                  {status.map((x, i) => (
                    <span key={i} className={`gb-dot gb-dot--${x}`} title={`Hand ${i + 1}`} />
                  ))}
                </span>
                <span className="gb-set-meta">
                  {s.hands.length} hands{played ? ` · ${played} played` : ""}
                </span>
              </span>
              <span className="gb-set-go">{played ? (played === s.hands.length ? "Play again" : "Continue") : "Start"} →</span>
            </Link>
          );
        })}
      </div>
      {visiblePractice(cat, isAdmin).length ? (
        <>
          <div className="gb-sets gb-sets--prac">
            {visiblePractice(cat, isAdmin).map((s) => {
              const status = s.hands.map((h) => handStatus(results, s.id, h));
              const played = status.filter((x) => x !== "new").length;
              const nextIdx = status.findIndex((x) => x === "new" || x === "part");
              const to = `${ROOT}/${cat.id}/${s.id}${played ? `?hand=${(nextIdx < 0 ? 0 : nextIdx) + 1}` : ""}`;
              return (
                <Link key={s.id} to={to} className="gb-pcard">
                  <span className="gb-pcard-ic" aria-hidden="true">✎</span>
                  <span className="gb-pcard-main">
                    <span className="gb-pcard-k">Practice</span>
                    <span className="gb-pcard-t"><Suited text={s.title} /></span>
                    <span className="gb-set-dots" aria-label={`${played} of ${s.hands.length} questions done`}>
                      {status.map((x, i) => (
                        <span key={i} className={`gb-dot gb-dot--${x}`} title={`Question ${i + 1}`} />
                      ))}
                    </span>
                    <span className="gb-pcard-m">
                      {s.hands.length} questions{played ? ` · ${played} done` : ""}
                    </span>
                  </span>
                  <span className="gb-pcard-go">{played ? (played === s.hands.length ? "Do again" : "Continue") : "Start"} →</span>
                </Link>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}

export const GuidedBiddingCategory = connect((state) => ({
  uid: state.auth?.uid || null,
  isAdmin: state.auth?.a === true,
  authReady: state.auth?.authReady === true,
}))(Category);

function Trainer({ match, location, history, uid, tier, subscriptionActive, isAdmin, authReady }) {
  const cat = findCategory(match.params.cat);
  const set = findSet(match.params.cat, match.params.set);

  const initialHand = useMemo(() => {
    const n = Number(new URLSearchParams(location.search).get("hand"));
    return set && n >= 1 && n <= set.hands.length ? n - 1 : 0;
  }, []);

  const [handIdx, setHandIdx] = useState(initialHand);
  const [step, setStep] = useState(0);
  const [calls, setCalls] = useState([]);
  const [wrongBids, setWrongBids] = useState([]);
  const [answered, setAnswered] = useState(null); // {correct, bid}
  const [done, setDone] = useState(false);
  const [newFrom, setNewFrom] = useState(-1);
  const [selLevel, setSelLevel] = useState(null);
  const [selStrain, setSelStrain] = useState(null);
  const [boxMsg, setBoxMsg] = useState("");
  const [hl, setHl] = useState(null);
  const [pinned, setPinned] = useState(null);
  const [artOpen, setArtOpen] = useState(false);
  const [results, setResults] = useState({});
  const [earned, setEarned] = useState(null); // hand id just scored
  const frameRef = useRef(null);

  const hand = set ? set.hands[handIdx] : null;
  // Practice sets call each item a question (Paul, 7 Oct 2026); ordinary sets play hands.
  const unit = set && set.kind === "practice" ? "Question" : "Hand";
  const article = hand ? ARTICLES[hand.article] : null;
  // A per-account grant opens ONE set (hands and videos) without a membership - guidedBiddingGrants/<uid>
  // (Paul, 30 Oct 2026: "I only want nick able to view that particular set, not every set").
  const [grantedSets, setGrantedSets] = useState([]);
  useEffect(() => {
    let live = true;
    loadSetGrants(uid).then((g) => live && setGrantedSets(g));
    return () => {
      live = false;
    };
  }, [uid]);
  const granted = !!set && grantedSets.includes(set.id);
  const isMember = !!subscriptionActive || isAdmin || granted;
  // Members only - except hand 1 of every set, which anyone can play (Paul, 26 Sep 2026).
  // The local dev copy (localhost) is never gated, so Paul can proofread local-only drafts without logging in there.
  const locked = handIdx > 0 && !isMember && !DEV_HOST;
  const here = `${ROOT}/${match.params.cat}/${match.params.set}?hand=${handIdx + 1}`;

  useEffect(() => {
    let live = true;
    loadResults(uid).then((r) => live && setResults(r));
    return () => {
      live = false;
    };
  }, [uid]);

  const loadHand = useCallback(
    (i) => {
      if (!set) return;
      setHandIdx(i);
      setStep(0);
      setCalls(set.hands[i].start.slice());
      setWrongBids([]);
      setAnswered(null);
      setDone(false);
      setNewFrom(-1);
      setSelLevel(null);
      setSelStrain(null);
      setBoxMsg("");
      setHl(null);
      setPinned(null);
      setArtOpen(false);
      history.replace({ search: `?hand=${i + 1}` });
      window.scrollTo(0, 0);
    },
    [set, history]
  );

  useEffect(() => {
    loadHand(initialHand);
  }, []);

  const lastBid = () => {
    for (let i = calls.length - 1; i >= 0; i--) if (isBid(calls[i])) return calls[i];
    return null;
  };

  // The bidding box starts with the lowest available level already selected (Paul, 28 Sep 2026: "for the bidding
  // level, select 1 if available etc"), so one tap on a suit makes the bid. Re-applied after a wrong attempt.
  useEffect(() => {
    if (!hand || !calls.length || answered || done || selStrain !== null) return;
    const lb = lastBid();
    const min = !lb ? 1 : Number(lb[0]) + (lb.slice(1) === "N" ? 1 : 0);
    // Set it when nothing is chosen, or when the chosen level is no longer available (e.g. set before the auction loaded).
    if (min <= 7 && (selLevel === null || selLevel < min)) setSelLevel(min);
  }, [calls, answered, done, selLevel, selStrain, hand]);

  const finishQuestion = (correct, bid, wrong) => {
    const s = hand.steps[step];
    // A multiple-choice question is about the auction as it stands - nothing is added to it.
    // An accepted alternative (step `accept`) goes into the auction as bid; otherwise the answer does.
    if (!s.choices) setCalls((c) => [...c, correct && bid ? bid : s.answer]);
    setNewFrom(-1);
    setAnswered({ correct, bid });
    const key = questionKey(set.id, hand.id, s.id);
    recordQuestion(uid, key, wrong).then(({ outcome }) => {
      setResults((r) => ({ ...r, [key]: { ...(r[key] || {}), last: outcome } }));
      // Opened from the Game Plan panel: tell it to ask the ladder again.
      try {
        if (window.self !== window.top) window.parent.postMessage({ type: "bc-month-result", guided: true }, window.location.origin);
      } catch (e) {
        /* not framed by us */
      }
    });
  };

  const makeBid = (b) => {
    if (done || answered) return;
    const s = hand.steps[step];
    setSelLevel(null);
    setSelStrain(null);
    setBoxMsg("");
    // `accept`: other calls Paul also counts as correct (Double or not hand 4, 10 Oct 2026: "count 4S correct").
    if (b === s.answer || (s.accept || []).includes(b)) return finishQuestion(true, b, wrongBids);
    const wrong = [...wrongBids, b];
    setWrongBids(wrong);
    if (wrong.length >= 2) finishQuestion(false, b, wrong);
  };

  const pickLevel = (l) => {
    const strain = selStrain;
    if (strain) return tryBid(l, strain);
    setSelLevel(l);
    setBoxMsg("");
  };
  const pickStrain = (s) => {
    const level = selLevel;
    if (level) return tryBid(level, s);
    setSelStrain(s);
    setBoxMsg("");
  };
  const tryBid = (l, s) => {
    const b = `${l}${s}`;
    const lb = lastBid();
    if (lb && rank(b) <= rank(lb)) {
      setBoxMsg(`${l}${s === "N" ? "NT" : SUIT_CHAR[s]} is not higher than the last bid`);
      setSelLevel(null);
      setSelStrain(s);
      return;
    }
    makeBid(b);
  };

  const next = () => {
    const nextStep = hand.steps[step + 1];
    setNewFrom(calls.length);
    setCalls((c) => [...c, ...nextStep.continuation]);
    setStep(step + 1);
    setWrongBids([]);
    setAnswered(null);
    setHl(null);
    setPinned(null);
  };

  const complete = () => {
    setDone(true);
    setNewFrom(-1);
    // Finishing a hand (right or wrong) scores GUIDED_POINTS once, ever, for members.
    recordHandDone(uid, questionKey(set.id, hand.id, "done")).then((firstTime) => {
      if (firstTime && isMember) setEarned(hand.id);
      try {
        if (FRAMED) window.parent.postMessage({ type: "bc-month-result", guided: true }, window.location.origin);
      } catch (e) {
        /* not framed by us */
      }
    });
    if (hand.final) {
      setCalls((c) => {
        const out = c.slice();
        while (!(out.length >= 3 && out.slice(-3).every((x) => x === "P"))) out.push("P");
        return out;
      });
    }
  };

  // No Finish button: the hand completes itself as soon as its last bid is answered (Paul, 28 Sep 2026).
  useEffect(() => {
    if (hand && answered && !done && step === hand.steps.length - 1) complete();
  }, [answered, done, step]);

  // When a hand with a video ends, bring the video button into view (Paul, 2 Oct 2026).
  useEffect(() => {
    if (!done || !hand || !hand.video) return;
    const t = setTimeout(() => {
      const b = document.getElementById("gb-watchbtn");
      if (b) b.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 400);
    return () => clearTimeout(t);
  }, [done]);

  // Enter continues; Escape closes the article.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") return setArtOpen(false);
      if (e.key !== "Enter" || artOpen) return;
      if (answered && !done) {
        e.preventDefault();
        if (step < hand.steps.length - 1) next();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Hide the site header/footer inside the article panel (same origin in production).
  const trimFrame = () => {
    try {
      const doc = frameRef.current && frameRef.current.contentDocument;
      if (!doc || doc.getElementById("gb-trim")) return;
      const st = doc.createElement("style");
      st.id = "gb-trim";
      st.textContent =
        ".bc-header,.bc-footer,.bc-breadcrumb{display:none!important}body{padding-top:0!important}";
      doc.head.appendChild(st);
    } catch (e) {
      /* cross-origin (localhost): panel shows the page as-is */
    }
  };

  if (set && set.draft && !isAdmin && !DEV_HOST) {
    if (!authReady) return null;
    return <Redirect to={ROOT} />;
  }
  if (!cat || !set) return <Redirect to={ROOT} />;
  if (!hand || !calls.length) return null;

  const s = hand.steps[step];
  const lastStep = step === hand.steps.length - 1;
  const pending = !answered && !done;
  const lb = lastBid();
  const minRank = lb ? rank(lb) + 1 : 0;
  // The popup shows either this hand's article or the category's general guide.
  const pop = artOpen ? ARTICLES[artOpen] : null;
  const guide = cat.guide ? ARTICLES[cat.guide] : null;
  const artHref =
    pop && typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)/.test(window.location.hostname)
      ? `https://bridgechampions.com${pop.path}`
      : pop && pop.path;

  // Auction rows (+ "Your turn" cell while South is to bid - not for a multiple-choice question).
  const cells = calls.map((c, i) => ({ c, i }));
  if (pending && !s.choices) cells.push({ c: "?", i: -1 });
  const rows = [];
  for (let i = 0; i < cells.length; i += 4) rows.push(cells.slice(i, i + 4));

  const onRefEvent = (e) => {
    const artLink = e.target.closest && e.target.closest("[data-art]");
    if (artLink && e.type === "click") {
      e.preventDefault();
      const key = artLink.getAttribute("data-art");
      setArtOpen(key && key !== "1" && ARTICLES[key] ? key : hand.article);
      return;
    }
    const ref = e.target.closest && e.target.closest(".gb-ref");
    if (e.type === "click") {
      if (ref) {
        const r = Number(ref.dataset.ref);
        setPinned(pinned === r ? null : r);
        setHl(pinned === r ? null : r);
      }
      return;
    }
    if (!ref) return;
    const r = Number(ref.dataset.ref);
    if (e.type === "mouseover" || e.type === "focus") setHl(r);
    if (e.type === "mouseout" || e.type === "blur") setHl(pinned);
  };

  const missedHand = (h) =>
    h.steps.some((st) => {
      const r = results[questionKey(set.id, h.id, st.id)];
      return r && r.last && r.last !== "first";
    });

  // Paul, 28 Sep 2026: the video slot is there from the start and opens as soon as the hand's last bid is answered.
  const videoOpen = !!hand.video && (done || (!!answered && step === hand.steps.length - 1));
  // A granted set shows its videos too, like the free first hand.
  const freeHand = handIdx === 0 || granted;
  const canWatch = freeHand || isAdmin || String(tier || "").startsWith("premium");

  return (
    <div className="gb-page">
      <Helmet>
        <title>{`${set.title} — Guided bidding — Bridge Champions`}</title>
        <meta name="robots" content="noindex" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@500;600;700&display=swap"
        />
      </Helmet>
      {FRAMED ? null : (
      <div className="gb-crumb">
        <Link to="/just-play">Just Play</Link> › <Link to={ROOT}>Guided bidding</Link> ›{" "}
        <Link to={`${ROOT}/${cat.id}`}>
          <Suited text={cat.label} />
        </Link>{" "}
        ›
      </div>
      )}
      <h1 className="gb-title gb-title--left"><Suited text={set.title} /></h1>
      {guide ? (
        <button type="button" className="gb-guide" onClick={() => setArtOpen(cat.guide)}>
          <span className="gb-guide-ic" aria-hidden="true">📘</span>
          <span className="gb-guide-t">
            <span className="gb-guide-k">General guide</span>
            <Suited text={guide.title} />
          </span>
          <span className="gb-guide-open">Open ›</span>
        </button>
      ) : null}
      {set.reading && ARTICLES[set.reading] ? (
        <button type="button" className="gb-guide gb-guide--reading" onClick={() => setArtOpen(set.reading)}>
          <span className="gb-guide-ic" aria-hidden="true">📗</span>
          <span className="gb-guide-t">
            <span className="gb-guide-k">Recommended reading</span>
            <Suited text={ARTICLES[set.reading].title} />
          </span>
          <span className="gb-guide-open">Open ›</span>
        </button>
      ) : null}

      {set.draft ? <div className="gb-draftbar">Draft - only admins can see this set. Not published.</div> : null}
      {/* The set note is a note BEFORE starting: hand 1 only, gone once the first bid is made (Paul, 28 Sep 2026:
          "get rid of this lasting message"). */}
      {handIdx === 0 && step === 0 && !answered && !done && (set.note || set.noteRule || set.noteVideo) ? (
        <div className="gb-setnote">
          <div className="gb-lbl">Before you start</div>
          {set.note
            ? String(set.note)
                .split(/\n\s*\n/)
                .map((para, pi) => (
                  <p key={pi} dangerouslySetInnerHTML={{ __html: suits(escapeHtml(para)).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>") }} />
                ))
            : null}
          {set.noteRule ? (
            <div className="gb-rule gb-rule--big">
              <b>Rule</b> <span dangerouslySetInnerHTML={{ __html: suits(escapeHtml(set.noteRule)) }} />
            </div>
          ) : null}
          {set.noteVideo ? <Video url={set.noteVideo} tier={tier} isMember={isMember} isAdmin={isAdmin} /> : null}
        </div>
      ) : null}

      <div className="gb-tabs" role="tablist">
        {set.hands.map((h, i) => (
          <button
            key={h.id}
            role="tab"
            aria-selected={i === handIdx}
            className={`gb-tab${i === handIdx ? " is-on" : ""}`}
            onClick={() => loadHand(i)}
            title={missedHand(h) ? "Worth another look" : undefined}
          >
            {unit} {i + 1}
            {i > 0 && !isMember && authReady && !DEV_HOST ? <span className="gb-tab-lock" aria-label="members only"> 🔒</span> : null}
            {missedHand(h) ? <span className="gb-tab-dot" aria-label="worth another look" /> : null}
          </button>
        ))}
      </div>
      <div className="gb-sub">
        {unit} {handIdx + 1} of {set.hands.length} · {done ? "Complete" : hand.steps.length > 1 ? `Bid ${step + 1} of ${hand.steps.length}` : unit === "Question" ? "Your answer" : `Bid ${step + 1} of ${hand.steps.length}`}
        {/* Paul, 30 Oct 2026: "make it clear that players get awarded points when they do it". */}
        <span className="gb-sub-pts">
          {set.kind === "practice" ? `+${PRACTICE_POINTS} Game Plan point for each question you finish` : `+${GUIDED_POINTS} Game Plan points for each hand you finish`}
        </span>
      </div>
      <div className="gb-steps">
        {hand.steps.map((st, i) => (
          <span key={st.id} className={i < step || done ? "is-done" : i === step ? "is-cur" : ""} />
        ))}
      </div>

      {locked ? (
        !authReady ? (
          <div className="gb-gate"><p>Checking access…</p></div>
        ) : (
          <div className="gb-gate">
            <div className="gb-gate-lock" aria-hidden="true">🔒</div>
            <h2>{unit} {handIdx + 1} is for members</h2>
            <p>{unit} 1 of every set is free. Become a member to play them all.</p>
            <div className="gb-gate-btns">
              <a href={`/membership?redirectTo=${encodeURIComponent(FRAMED ? "/game-plan" : here)}`} target="_top" className="gb-go gb-go--inline">
                View membership
              </a>
              {!uid ? (
                <a href={`/login?redirectTo=${encodeURIComponent(FRAMED ? "/game-plan" : here)}`} target="_top" className="gb-gate-login">
                  Already a member? Log in
                </a>
              ) : null}
            </div>
            <button type="button" className="gb-again" onClick={() => loadHand(0)}>
              Back to {unit.toLowerCase()} 1
            </button>
          </div>
        )
      ) : null}

      {!locked && (hand.intro || hand.rule) ? (
        <div className="gb-intro-wrap" onMouseOver={onRefEvent} onMouseOut={onRefEvent} onFocus={onRefEvent} onBlur={onRefEvent} onClick={onRefEvent}>
          {hand.intro ? (
            <div className="gb-intro">
              <div className="gb-lbl">Introduction</div>
              {/* Same markup as the commentary: bids link to the auction, {{art:key|text}} opens an article (Compete more set, 29 Oct 2026). */}
              <span dangerouslySetInnerHTML={{ __html: commentHtml(hand.intro, calls) }} />
            </div>
          ) : null}
          {hand.rule ? (
            <div className="gb-rule">
              <b>Rule</b> <span dangerouslySetInnerHTML={{ __html: suits(escapeHtml(hand.rule)) }} />
            </div>
          ) : null}
        </div>
      ) : null}

      {locked ? null : (
      <div className={`gb-grid${hand.south ? "" : " gb-grid--nohand"}`}>
          {hand.south ? (
          <div className="gb-card gb-card--hand gb-area-hand">
            <h3 className="gb-h3">Your hand</h3>
            <span className="gb-seat">South (you)</span>
            <div className="gb-bighand gb-bighand--one"><HandRows hand={hand.south} /></div>
            {/* Phones: majors and minors on two rows, so the cards barely overlap (BBO-like). */}
            <div className="gb-bighand gb-bighand--two" aria-hidden="true">
              <HandRows hand={hand.south} suits={["S", "H"]} />
              <HandRows hand={hand.south} suits={["C", "D"]} />
            </div>
          </div>
          ) : null}
          <div className="gb-card gb-card--focus gb-area-auc">
            <h3 className="gb-h3 gb-h3--focus">
              Auction
              {hand.vul ? <span className="gb-vul">{hand.vul}</span> : null}
            </h3>
            <table className="gb-auc">
              <thead>
                <tr>
                  <th>West</th>
                  <th>North</th>
                  <th>East</th>
                  <th className="gb-you">South</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, ri) => (
                  <tr key={ri}>
                    {[0, 1, 2, 3].map((k) => {
                      const cell = r[k];
                      if (!cell) return <td key={k} />;
                      if (cell.c === "?")
                        return (
                          <td key={k} className="gb-turn">
                            Your
                            <br />
                            turn
                          </td>
                        );
                      const cls = [newFrom >= 0 && cell.i >= newFrom ? "is-new" : "", hl === cell.i ? "is-hl" : "", s && s.ringCall === cell.i ? "is-ring" : ""]
                        .filter(Boolean)
                        .join(" ");
                      return <td key={k} className={cls} dangerouslySetInnerHTML={{ __html: callHtml(cell.c) }} />;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            {pending && s.choices ? (
              <div className="gb-play gb-choice">
                <p className="gb-choice-q" dangerouslySetInnerHTML={{ __html: suits(escapeHtml(s.question || "")) }} />
                {wrongBids.length === 1 ? (
                  <div className="gb-fb gb-fb--bad">
                    <b dangerouslySetInnerHTML={{ __html: suits(escapeHtml(wrongBids[0])) }} /> - not the one. Try again.
                  </div>
                ) : null}
                <div className="gb-choice-opts">
                  {s.choices.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className="gb-choice-opt"
                      disabled={wrongBids.includes(c)}
                      onClick={() => makeBid(c)}
                      dangerouslySetInnerHTML={{ __html: suits(escapeHtml(c)) }}
                    />
                  ))}
                </div>
                <div className="gb-hint">{wrongBids.length === 0 ? "Two attempts" : "1 attempt left"}</div>
              </div>
            ) : null}

            {pending && !s.choices ? (
              <div className="gb-play">
                {/* A question shown with this one bid (Competing set, Paul 14 Oct 2026: "You are in the passout seat..."). */}
                {s.prompt ? <p className="gb-choice-q" dangerouslySetInnerHTML={{ __html: suits(escapeHtml(s.prompt)) }} /> : null}
                {wrongBids.length === 1 ? (
                  <div className="gb-fb gb-fb--bad">
                    <b dangerouslySetInnerHTML={{ __html: callHtml(wrongBids[0]) }} /> - not the one. Try again.
                  </div>
                ) : null}
                <div className="gb-levels">
                  {[1, 2, 3, 4, 5, 6, 7].map((l) => {
                    const any = ORDER.some((x) => rank(l + x) >= minRank);
                    return (
                      <button
                        key={l}
                        type="button"
                        disabled={!any}
                        className={selLevel === l ? "is-sel" : ""}
                        onClick={() => pickLevel(l)}
                      >
                        {l}
                      </button>
                    );
                  })}
                </div>
                <div className="gb-strains">
                  {ORDER.map((x) => (
                    <button
                      key={x}
                      type="button"
                      className={selStrain === x ? "is-sel" : ""}
                      onClick={() => pickStrain(x)}
                      dangerouslySetInnerHTML={{ __html: x === "N" ? "NT" : suitSpan(x) }}
                    />
                  ))}
                </div>
                <div className="gb-calls">
                  <button type="button" className="gb-pass" onClick={() => makeBid("P")}>
                    Pass
                  </button>
                  <button type="button" className="gb-dbl" onClick={() => makeBid("X")}>
                    X
                  </button>
                  <button type="button" className="gb-rdbl" onClick={() => makeBid("XX")}>
                    XX
                  </button>
                </div>
                <div className="gb-hint">
                  {boxMsg ? <Suited text={boxMsg} /> :
                    (selLevel && !selStrain
                      ? "Now pick the suit"
                      : selStrain && !selLevel
                      ? "Now pick the level"
                      : "Pick a level and a suit")}
                  {"  ·  "}
                  {wrongBids.length === 0 ? "Two attempts" : "1 attempt left"}
                </div>
              </div>
            ) : null}

            {answered ? (
              <div className="gb-out" onMouseOver={onRefEvent} onMouseOut={onRefEvent} onFocus={onRefEvent} onBlur={onRefEvent} onClick={onRefEvent}>
                {answered.correct ? (
                  <div className="gb-fb gb-fb--good">
                    <b>
                      Correct - <span dangerouslySetInnerHTML={{ __html: s.choices ? suits(escapeHtml(s.answer)) : callHtml(answered.bid || s.answer) }} />
                    </b>
                  </div>
                ) : (
                  <div className="gb-fb gb-fb--shown">
                    {s.choices ? "You chose" : "You bid"}{" "}
                    <b dangerouslySetInnerHTML={{ __html: s.choices ? suits(escapeHtml(answered.bid)) : callHtml(answered.bid) }} />. The answer is{" "}
                    <b dangerouslySetInnerHTML={{ __html: s.choices ? suits(escapeHtml(s.answer)) : callHtml(s.answer) }} />.
                  </div>
                )}
                <div className="gb-comm">
                  <div className="gb-lbl gb-lbl--comm">Commentary</div>
                  {/* The answer stands out on its own (Paul, 30 Sep 2026: "make the answer clear ... highlighted or
                      shiny", so the commentary never has to start "bid 2H"). */}
                  <div className="gb-answer" key={`ans-${hand.id}-${s.id}`}>
                    <span className="gb-answer-k">Answer</span>
                    <span className={`gb-answer-bid${s.choices ? " gb-answer-bid--text" : ""}`} dangerouslySetInnerHTML={{ __html: s.choices ? suits(escapeHtml(s.answer)) : callHtml(s.answer) }} />
                  </div>
                  <div className="gb-cm" dangerouslySetInnerHTML={{ __html: commentLayout(s.comment, calls) }} />
                  {/* The alternatives, each with Paul's verdict (2 over 1 hand 3, 9 Oct 2026: "(ai put cross) Pass ... (tick) 2S"). */}
                  {s.options ? (
                    <ul className="gb-options">
                      {s.options.map((o, oi) => (
                        <li className={`gb-opt ${o.ok ? "gb-opt--yes" : "gb-opt--no"}`} key={oi}>
                          <span className="gb-opt-mark" aria-label={o.ok ? "Yes" : "No"}>{o.ok ? "✓" : "✗"}</span>
                          <span className="gb-opt-call" dangerouslySetInnerHTML={{ __html: callHtml(o.call) }} />
                          <span className="gb-opt-text" dangerouslySetInnerHTML={{ __html: commentHtml(o.text, calls) }} />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {s.rule ? (
                    <div className="gb-rule">
                      <b>Rule</b> <span dangerouslySetInnerHTML={{ __html: commentHtml(s.rule, calls) }} />
                    </div>
                  ) : null}
                  {/* `aside`: a side note in its own box after the commentary (Paul, 29 Oct 2026: "put this in a box, its a note, not main point"). */}
                  {s.aside ? (
                    <div className="gb-aside">
                      <div className="gb-lbl">Note</div>
                      <div dangerouslySetInnerHTML={{ __html: commentLayout(s.aside, calls) }} />
                    </div>
                  ) : null}
                </div>
                {(Array.isArray(s.extra) ? s.extra : s.extra ? [s.extra] : []).map((ex, xi) => (
                  <div className="gb-extra" key={xi}>
                    {ex.title ? <h4 className="gb-extra-t" dangerouslySetInnerHTML={{ __html: suits(escapeHtml(ex.title)) }} /> : null}
                    {ex.auction && ex.dealer ? (
                      /* With a dealer given, the side auction is a proper W/N/E/S table like the main one (Paul, 29 Oct 2026). */
                      <table className="gb-auc gb-auc--extra">
                        <thead>
                          <tr>
                            <th>West</th>
                            <th>North</th>
                            <th>East</th>
                            <th className="gb-you">South</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const pad = { W: 0, N: 1, E: 2, S: 3 }[ex.dealer] || 0;
                            const seq = [...Array(pad).fill(null), ...ex.auction];
                            const rws = [];
                            for (let i = 0; i < seq.length; i += 4) rws.push(seq.slice(i, i + 4));
                            return rws.map((r, ri) => (
                              <tr key={ri}>
                                {[0, 1, 2, 3].map((k) =>
                                  r[k] == null ? <td key={k} /> : <td key={k} dangerouslySetInnerHTML={{ __html: callHtml(r[k]) }} />
                                )}
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    ) : ex.auction ? (
                      <div className="gb-extra-auc">
                        {ex.auction.map((c, ci) => (
                          <React.Fragment key={ci}>
                            {ci ? <span className="gb-extra-auc-sep" aria-hidden="true">–</span> : null}
                            <span dangerouslySetInnerHTML={{ __html: callHtml(c) }} />
                          </React.Fragment>
                        ))}
                      </div>
                    ) : null}
                    {ex.lead ? <p dangerouslySetInnerHTML={{ __html: commentHtml(ex.lead, calls) }} /> : null}
                    {ex.hands ? (
                      <div className={`gb-diagram${ex.hands.length === 1 ? " gb-diagram--one" : ""}`}>
                        {ex.hands.map((h, i) => (
                          <div className="gb-dh" key={i}>
                            <span className="gb-seat">{h.label}</span>
                            <HandRows hand={h.hand} small />
                          </div>
                        ))}
                      </div>
                    ) : ex.north ? (
                      <div className="gb-diagram">
                        <div className="gb-dh">
                          <span className="gb-seat">North</span>
                          <HandRows hand={ex.north} small />
                        </div>
                        <div className="gb-dh">
                          <span className="gb-seat">South</span>
                          <HandRows hand={hand.south} small />
                        </div>
                      </div>
                    ) : null}
                    {ex.after ? <div className="gb-cm" dangerouslySetInnerHTML={{ __html: commentLayout(ex.after, calls) }} /> : null}
                    {ex.tiles ? (
                      <div className="gb-tiles">
                        {ex.tiles.map((t, ti) => (
                          <div className="gb-tile" key={ti}>
                            <span className="gb-tile-big">{t.big}</span>
                            <span className="gb-tile-l">{t.label}</span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
                {s.flow ? (
                  <div className="gb-flow">
                    <div className="gb-lbl gb-lbl--comm">How the auction developed</div>
                    {s.flow.steps.map((f, i) => (
                      <React.Fragment key={i}>
                        {i ? <div className="gb-flow-arrow" aria-hidden="true">↓</div> : null}
                        <div className="gb-flow-step gb-ref" tabIndex={0} data-ref={f.ref}>
                          <span className="gb-flow-n">{i + 1}</span>
                          <span className="gb-flow-call" dangerouslySetInnerHTML={{ __html: callHtml(calls[f.ref] || "") }} />
                          <span dangerouslySetInnerHTML={{ __html: suits(escapeHtml(f.text)) }} />
                        </div>
                      </React.Fragment>
                    ))}
                    {s.flow.after ? <p className="gb-flow-after" dangerouslySetInnerHTML={{ __html: suits(escapeHtml(s.flow.after)) }} /> : null}
                  </div>
                ) : null}
                {s.video ? <Video url={s.video} tier={tier} isMember={isMember} isAdmin={isAdmin} free={freeHand} /> : null}
                {/* The hand's article sits here, after the answer - not beside the auction, where it competed with
                    the bidding box (Paul, 28 Sep 2026: "it makes the CTA less clear"). */}
                {article ? (
                  <button type="button" className="gb-artline" onClick={() => setArtOpen(hand.article)}>
                    <span aria-hidden="true">📖</span> Related: <b><Suited text={article.title} /></b> ›
                  </button>
                ) : null}
                {!lastStep && !done ? (
                  <button type="button" className="gb-go" onClick={next}>
                    Continue the auction →
                  </button>
                ) : null}
              </div>
            ) : null}

            {done ? (
              <div className="gb-out">
                <div className="gb-fb gb-fb--good">
                  <b>
                    {hand.final ? (
                      <>
                        Final contract: <span dangerouslySetInnerHTML={{ __html: suits(escapeHtml(hand.final)) }} />
                      </>
                    ) : (
                      `${unit} complete`
                    )}
                  </b>
                </div>
                {/* Paul, 2 Oct 2026: "people aren't noticing the videos ... make it in their face when the problem ends".
                    A big button that takes you straight to the video, and the page scrolls to it when the hand ends. */}
                {hand.video ? (
                  <button
                    type="button"
                    id="gb-watchbtn"
                    className={`gb-watchbtn${canWatch ? "" : " gb-watchbtn--locked"}`}
                    onClick={() => {
                      const v = document.getElementById("gb-vidslot");
                      if (v) v.scrollIntoView({ behavior: "smooth", block: "center" });
                    }}
                  >
                    <span className="gb-watchbtn-ic" aria-hidden="true">{canWatch ? "▶" : "🔒"}</span>
                    <span>
                      <b>{canWatch ? "Watch Paul's video explanation" : "Upgrade to watch Paul's video explanation"}</b>
                      <small>{canWatch ? "The video for this hand is ready below" : "Premium & yearly members"}</small>
                    </span>
                  </button>
                ) : null}
                {earned === hand.id ? (
                  <div className="gb-points" role="status">{set.kind === "practice" ? `+${PRACTICE_POINTS} point` : `+${GUIDED_POINTS} points`} on the Game Plan ladder</div>
                ) : null}
                {handIdx < set.hands.length - 1 ? (
                  <button type="button" className="gb-go" onClick={() => loadHand(handIdx + 1)}>
                    Next {unit.toLowerCase()} →
                  </button>
                ) : (
                  FRAMED ? (
                    <button type="button" className="gb-go" onClick={() => tellParent("bc-month-close")}>
                      Back to Game Plan
                    </button>
                  ) : (
                    <Link className="gb-go gb-go--suits" to={`${ROOT}/${cat.id}`}>
                      Back to <Suited text={cat.label} /> →
                    </Link>
                  )
                )}
                <button type="button" className="gb-again" onClick={() => loadHand(handIdx)}>
                  Start this {unit.toLowerCase()} again
                </button>
              </div>
            ) : null}
          </div>
      </div>
      )}

      {!locked && hand.video ? (
        <div id="gb-vidslot" className={`gb-vidslot${videoOpen ? " is-open" : ""}`}>
          <div className="gb-vidslot-h">
            <span aria-hidden="true">🎬</span> Video explanation
          </div>
          {videoOpen ? (
            <Video url={hand.video} tier={tier} isMember={isMember} isAdmin={isAdmin} free={freeHand} />
          ) : (
            <div className="gb-vidslot-wait">
              <span className="gb-vidslot-play" aria-hidden="true">▶</span>
              <span>
                Video explanation is only available at the end of the hand
                {canWatch ? "" : <small>Premium &amp; yearly members</small>}
              </span>
            </div>
          )}
        </div>
      ) : null}

      {artOpen ? (
        <div className="gb-modal" onClick={(e) => e.target === e.currentTarget && setArtOpen(false)}>
          <div className="gb-panel" role="dialog" aria-label={pop.title}>
            <header>
              <b><Suited text={pop.title} /></b>
              <a href={artHref} target="_blank" rel="noopener noreferrer">
                New tab ↗
              </a>
              <button type="button" aria-label="Close" onClick={() => setArtOpen(false)}>
                ×
              </button>
            </header>
            <iframe key={artOpen} ref={frameRef} src={artHref} title={pop.title} onLoad={trimFrame} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

const mapStateToProps = (state) => ({
  uid: state.auth?.uid || null,
  tier: state.auth?.tier ?? "basic",
  subscriptionActive: state.auth?.subscriptionActive === true,
  isAdmin: state.auth?.a === true,
  // Signed in but the members record has not arrived yet = still checking (not locked out).
  authReady:
    state.auth?.authReady === true &&
    (!state.auth?.uid || state.auth?.subscriptionKnown === true || state.auth?.a === true),
});

export const GuidedBiddingTrainer = connect(mapStateToProps)(Trainer);
